import {
  Avatar,
  Button,
  Card,
  Col,
  Divider,
  Row,
  Space,
  Typography,
  Tag,
  Statistic,
  Empty,
  Spin,
} from "antd";
import {
  BarChartOutlined,
  BookOutlined,
  ClockCircleOutlined,
  CommentOutlined,
  EyeOutlined,
  FundViewOutlined,
  HeartOutlined,
  LineChartOutlined,
  PlayCircleOutlined,
  ShareAltOutlined,
  TeamOutlined,
  TagsOutlined,
  ThunderboltOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { useUserStore } from "../../../stores/userStore";
import { useState } from "react";
import Router from "../..";
import { useSocialData } from "../../hooks/useSocialData";
import { CATEGORY_MAPPING, CATEGORY_STYLES } from "../../utils/categories";
import { formatCnt } from "../../utils/formatCnt";
import dayjs from "dayjs";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";
import { getNiceDomain, getNiceStep } from "../../utils/chartScale";

const { Title, Paragraph } = Typography;

const formatWeek = (weekStr) => {
  if (!weekStr) return "";

  const match = weekStr.match(/(\d{4})-W(\d+)/);
  if (!match) return weekStr;

  const year = Number(match[1]);
  const week = Number(match[2]);

  return dayjs(`${year}-01-04`).isoWeek(week).startOf("isoWeek").format("M/D");
};

const prepareSnapshotTrends = (snapshots) => {
  if (!snapshots || snapshots.length === 0)
    return {
      followers: [],
      likes: [],
      contents: [],
      avgViews: [],
      avgPlays: [],
      totalWatchTime: [],
      avgWatchTime: [],
      avgReach: [],
      avgImpressions: [],
      avgLikes: [],
      avgComments: [],
      avgCollects: [],
      avgShares: [],
    };

  const sorted = [...snapshots].sort((a, b) =>
    a.snapshot_week.localeCompare(b.snapshot_week)
  );

  return {
    followers: sorted
      .filter(
        (snap) =>
          snap.follower_count !== null && snap.follower_count !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.follower_count) || 0,
      })),
    likes: sorted
      .filter(
        (snap) => snap.like_count !== null && snap.like_count !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.like_count) || 0,
      })),
    contents: sorted
      .filter(
        (snap) =>
          snap.content_count !== null && snap.content_count !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.content_count) || 0,
      })),
    avgViews: sorted
      .filter(
        (snap) =>
          snap.average_views !== null && snap.average_views !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.average_views) || 0,
      })),
    avgPlays: sorted
      .filter(
        (snap) =>
          snap.average_plays !== null && snap.average_plays !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.average_plays) || 0,
      })),
    totalWatchTime: sorted
      .filter(
        (snap) =>
          snap.reels_total_watch_time !== null &&
          snap.reels_total_watch_time !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.reels_total_watch_time) || 0,
      })),
    avgWatchTime: sorted
      .filter(
        (snap) =>
          snap.reels_avg_watch_time !== null &&
          snap.reels_avg_watch_time !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.reels_avg_watch_time) || 0,
      })),
    avgReach: sorted
      .filter(
        (snap) =>
          snap.average_reach !== null && snap.average_reach !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.average_reach) || 0,
      })),
    avgImpressions: sorted
      .filter(
        (snap) =>
          snap.average_impressions !== null &&
          snap.average_impressions !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.average_impressions) || 0,
      })),
    avgLikes: sorted
      .filter(
        (snap) =>
          snap.average_likes !== null && snap.average_likes !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.average_likes) || 0,
      })),
    avgComments: sorted
      .filter(
        (snap) =>
          snap.average_comments !== null && snap.average_comments !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.average_comments) || 0,
      })),
    avgCollects: sorted
      .filter(
        (snap) =>
          snap.average_collects !== null && snap.average_collects !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.average_collects) || 0,
      })),
    avgShares: sorted
      .filter(
        (snap) =>
          snap.average_shares !== null && snap.average_shares !== undefined
      )
      .map((snap) => ({
        week: formatWeek(snap.snapshot_week),
        value: parseInt(snap.average_shares) || 0,
      })),
  };
};

// 섹션 제목 컴포넌트
const SectionTitle = ({ children }) => (
  <h3 className="mb-5 text-[18px] font-semibold text-[#262626] pb-3 border-b-2 border-[#F0F0F0]">
    {children}
  </h3>
);

// 통계 카드 컴포넌트
const TrendCard = ({ title, data }) => {
  const validData = data.filter(
    (d) => d.week && d.week !== "undefined/undefined"
  );

  const values = validData.map((d) => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);

  const [niceMin, niceMax] = getNiceDomain(min, max, 4);
  const yStep = getNiceStep(min, max, 4);

  const ticks = [];
  for (let v = niceMin; v <= niceMax; v += yStep) ticks.push(v);

  const gradientId = `gradient-${title.replace(/[^a-zA-Z0-9]/g, "")}`;

  const MAX_X_TICKS = 5;
  const xTickLabels = validData.map((d) => d.week);

  const xStep = Math.ceil(xTickLabels.length / MAX_X_TICKS);

  const xTicks = xTickLabels.filter((_, i) => i % xStep === 0);

  if (xTicks.at(-1) !== xTickLabels.at(-1)) {
    xTicks.push(xTickLabels.at(-1));
  }

  return (
    <Card
      styles={{
        body: {
          padding: "20px",
          background: "#FAFAFA",
          borderRadius: "8px",
        },
      }}
      className="rounded-[8px] border border-[#E8E8E8]"
    >
      <div className="mb-4">
        <span className="text-[14px] font-semibold text-[#262626]">
          {title}
        </span>
      </div>
      {validData.length > 0 ? (
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart
            data={validData}
            margin={{ top: 10, right: 20, left: -10, bottom: 0 }}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#F9A43F" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#F9A43F" stopOpacity={0.05} />
              </linearGradient>
            </defs>

            <CartesianGrid stroke="#F3F4F6" vertical={false} />
            <XAxis
              dataKey="week"
              tick={{ fontSize: 11, fill: "#666" }}
              axisLine={{ stroke: "#D9D9D9" }}
              tickLine={{ stroke: "#D9D9D9" }}
              ticks={xTicks}
              tickMargin={6}
              preserveStartEnd
              padding={{ left: 0, right: 0 }}
            />
            <YAxis
              domain={[niceMin, niceMax]}
              ticks={ticks}
              tick={{ fontSize: 11, fill: "#666" }}
              tickFormatter={(value) => formatCnt(value)}
              axisLine={false}
              tickLine={false}
              width={60}
            />
            <Tooltip
              formatter={(value) => [formatCnt(value), title]}
              labelStyle={{ fontSize: 12, fontWeight: 600 }}
              contentStyle={{
                fontSize: 11,
                borderRadius: "6px",
                border: "1px solid #E8E8E8",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                padding: "8px 12px",
              }}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke="#F9A43F"
              strokeWidth={2.5}
              fill={`url(#${gradientId})`}
              dot={false}
              activeDot={{ r: 5 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      ) : (
        <Empty
          description="데이터 없음"
          style={{ padding: "50px 0" }}
          imageStyle={{ height: 60 }}
        />
      )}
    </Card>
  );
};

export const InstagramTabContent = () => {
  const { userInfo } = useUserStore();
  const [instagramRegisterLoading, setInstagramRegisterLoading] =
    useState(false);
  const { data: instagramData, isLoading } = useSocialData(
    "instagram",
    userInfo?.type_info?.instagram_influencer_id
  );

  const influencerInfo = instagramData?.influencer_info ?? {};
  const categories = instagramData?.category ?? [];
  const trends = prepareSnapshotTrends(instagramData?.snapshot);
  const recentContents = influencerInfo?.recent_contents?.items ?? [];

  const INSTAGRAM_CLIENT_ID = process.env.REACT_APP_INSTAGRAM_CLIENT_ID;
  const INSTAGRAM_AUTH_URL = `https://www.instagram.com/oauth/authorize?force_reauth=true&client_id=${INSTAGRAM_CLIENT_ID}&redirect_uri=https://app.surfingcre.com/auth-success/&response_type=code&scope=instagram_business_basic%2Cinstagram_business_manage_messages%2Cinstagram_business_manage_comments%2Cinstagram_business_content_publish%2Cinstagram_business_manage_insights`;

  // 팝업 인증 처리 함수
  const handleInstagramAuth = () => {
    const popup = window.open(
      INSTAGRAM_AUTH_URL,
      "instagramAuthPopup",
      "width=500,height=700"
    );
    let handled = false;

    const timer = setInterval(async () => {
      try {
        if (!popup || popup.closed) {
          clearInterval(timer);
          return;
        }
        const url = popup.location.href;
        if (
          !handled &&
          url.startsWith("https://app.surfingcre.com/auth-success/")
        ) {
          handled = true;
          const params = new URL(url).searchParams;
          const code = params.get("code");
          if (code) {
            clearInterval(timer);
            setInstagramRegisterLoading(true);
            const registered = await Router.Api.registerInstagramCode(code);
            if (registered == null) {
              setInstagramRegisterLoading(false);
              return;
            }
            popup.close();
            window.location.href = "/influencer/dashboard?tab=instagram";
          }
        }
      } catch (e) {
        console.error(e);
      }
    }, 500);
  };

  return (
    <div className="w-full max-w-[1212.5px] p-4 bg-white rounded-tr-[10px] rounded-bl-[10px] rounded-br-[10px] border border-[#d9d9d9] shadow-[0_2px_6px_rgba(0,0,0,0.1)] overflow-x-hidden">
      {isLoading || instagramRegisterLoading ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <Spin size="large" />
        </div>
      ) : (
        <>
          {userInfo?.type_info?.instagram_influencer_id === null ? (
            <div className="flex flex-col items-center py-10 px-5 text-center">
              <Space direction="vertical" size="large" className="w-full">
                <div>
                  <Title
                    level={3}
                    className="mb-2 text-[#262626] text-[20px] sm:text-[24px] md:text-[28px] lg:text-[32px]"
                  >
                    인스타그램 계정을 연동해주세요
                  </Title>
                  <Paragraph className="text-[14px] sm:text-[16px] md:text-[18px] text-[#595959] mb-0">
                    인스타그램 계정을 연동하여 데이터를 확인할 수 있습니다
                  </Paragraph>
                  <Paragraph className="text-[12px] sm:text-[13px] md:text-[14px] text-[#F5222D] mt-2 mb-0">
                    연동 전 인스타그램 계정이 프로페셔널(비즈니스/크리에이터)
                    계정으로 전환되어 있는지 확인해주세요. 일반 개인 계정은
                    연동이 정상적으로 되지 않습니다.
                  </Paragraph>
                </div>

                <Button
                  type="primary"
                  size="large"
                  onClick={handleInstagramAuth}
                  className="
                !bg-gradient-to-r
                from-[#5851DB]
                via-[#C13584]
                to-[#F77737]
                border-0
                hover:opacity-90
                h-12 sm:h-14 md:h-16
                text-[16px] sm:text-[18px] md:text-[20px]
                font-semibold
                px-8 sm:px-10 md:px-12
                rounded-lg
                shadow-[0_2px_12px_rgba(193,53,132,0.35)]
              "
                >
                  인스타그램 인증하기
                </Button>

                <Divider className="my-8 text-[14px] sm:text-[16px] md:text-[18px]">
                  인증 후 이용 가능한 기능
                </Divider>

                <Row gutter={[24, 24]}>
                  <Col xs={24} md={8}>
                    <Card
                      hoverable
                      className="text-center rounded-xl border border-[#e8e8e8] h-full"
                      styles={{ body: { padding: "20px" } }}
                    >
                      <BarChartOutlined className="text-[32px] sm:text-[40px] md:text-[48px] text-[#C13584] mb-3" />
                      <div className="text-[14px] sm:text-[16px] md:text-[18px] font-semibold text-[#262626]">
                        채널 통계 확인
                      </div>
                      <div className="text-[12px] sm:text-[14px] md:text-[16px] text-[#8c8c8c] mt-1">
                        팔로워, 조회수, 좋아요 등
                      </div>
                    </Card>
                  </Col>

                  <Col xs={24} md={8}>
                    <Card
                      hoverable
                      className="text-center rounded-xl border border-[#e8e8e8] h-full"
                      styles={{ body: { padding: "20px" } }}
                    >
                      <TagsOutlined className="text-[32px] sm:text-[40px] md:text-[48px] text-[#C13584] mb-3" />
                      <div className="text-[14px] sm:text-[16px] md:text-[18px] font-semibold text-[#262626]">
                        카테고리 설정
                      </div>
                      <div className="text-[12px] sm:text-[14px] md:text-[16px] text-[#8c8c8c] mt-1">
                        전문 분야 선택
                      </div>
                    </Card>
                  </Col>

                  <Col xs={24} md={8}>
                    <Card
                      hoverable
                      className="text-center rounded-xl border border-[#e8e8e8] h-full"
                    >
                      <LineChartOutlined className="text-[32px] sm:text-[40px] md:text-[48px] text-[#C13584] mb-3" />
                      <div className="text-[14px] sm:text-[16px] md:text-[18px] font-semibold text-[#262626]">
                        데이터 분석
                      </div>
                      <div className="text-[12px] sm:text-[14px] md:text-[16px] text-[#8c8c8c] mt-1">
                        주간 추이, 인게이지먼트 등
                      </div>
                    </Card>
                  </Col>
                </Row>
              </Space>
            </div>
          ) : (
            <div>
              <div>
                <Card className="mb-6 rounded-xl border border-[#E8E8E8]">
                  <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-12">
                    <Avatar
                      src={influencerInfo.profile_img}
                      className="!w-[72px] !h-[72px] md:!w-[96px] md:!h-[96px] border-4 border-[#F0F0F0]"
                    />

                    <div className="min-w-0">
                      <div className="mb-2 flex flex-wrap gap-1">
                        {categories.map((category, idx) => (
                          <Tag
                            key={idx}
                            className="border-none px-2 py-0.5 text-[11px] md:text-[12px] font-medium rounded-lg m-0"
                            style={
                              CATEGORY_STYLES[category] || CATEGORY_STYLES.etc
                            }
                          >
                            {CATEGORY_MAPPING[category] || category}
                          </Tag>
                        ))}
                      </div>

                      <div className="flex items-center gap-2">
                        <p className="m-0 text-[18px] md:text-[22px] font-bold leading-tight break-words">
                          {influencerInfo.nickname || "-"}
                        </p>
                      </div>

                      <p className="text-[#8C8C8C] m-0 text-[12px] md:text-[13px] break-words">
                        @{influencerInfo.unique_id || "-"}
                      </p>

                      {influencerInfo.bio && (
                        <p className="text-[#595959] text-[12px] md:text-[13px] mt-2 leading-relaxed break-words">
                          {influencerInfo.bio}
                        </p>
                      )}
                    </div>
                  </div>
                </Card>

                {/* 채널 기본 통계 */}
                <div className="mb-8">
                  <Card
                    title="채널 통계"
                    className="rounded-xl border border-[#E8E8E8]"
                    styles={{ body: { padding: "24px" } }}
                  >
                    <Row gutter={[16, 16]}>
                      <Col xs={12} lg={8}>
                        <Statistic
                          title={
                            <span className="text-[13px] text-[#8C8C8C]">
                              팔로워
                            </span>
                          }
                          value={influencerInfo.follower_count}
                          prefix={
                            <UserOutlined className="text-[#5B8FF9] text-[22px] md:text-[24px]" />
                          }
                          formatter={(value) => (
                            <span className="text-[22px] md:text-[24px] font-semibold">
                              {formatCnt(value)}
                            </span>
                          )}
                        />
                      </Col>
                      <Col xs={12} lg={8}>
                        <Statistic
                          title={
                            <span className="text-[13px] text-[#8C8C8C]">
                              총 좋아요
                            </span>
                          }
                          value={influencerInfo.like_count}
                          prefix={
                            <HeartOutlined className="text-[#FF4D4F] text-[22px] md:text-[24px]" />
                          }
                          formatter={(value) => (
                            <span className="text-[22px] md:text-[24px] font-semibold">
                              {formatCnt(value)}
                            </span>
                          )}
                        />
                      </Col>
                      <Col xs={12} lg={8}>
                        <Statistic
                          title={
                            <span className="text-[13px] text-[#8C8C8C]">
                              평균 좋아요
                            </span>
                          }
                          value={influencerInfo.average_likes}
                          prefix={
                            <HeartOutlined className="text-[#FF4D4F] text-[22px] md:text-[24px]" />
                          }
                          formatter={(value) => (
                            <span className="text-[22px] md:text-[24px] font-semibold">
                              {formatCnt(value)}
                            </span>
                          )}
                        />
                      </Col>
                      <Col xs={12} lg={8}>
                        <Statistic
                          title={
                            <span className="text-[13px] text-[#8C8C8C]">
                              총 영상
                            </span>
                          }
                          value={influencerInfo.content_count}
                          prefix={
                            <VideoCameraOutlined className="text-[#722ED1] text-[22px] md:text-[24px]" />
                          }
                          formatter={(value) => (
                            <span className="text-[22px] md:text-[24px] font-semibold">
                              {value}
                            </span>
                          )}
                        />
                      </Col>
                      <Col xs={12} lg={8}>
                        <Statistic
                          title={
                            <span className="text-[13px] text-[#8C8C8C]">
                              평균 조회수
                            </span>
                          }
                          value={influencerInfo.average_views}
                          prefix={
                            <EyeOutlined className="text-[#13C2C2] text-[22px] md:text-[24px]" />
                          }
                          formatter={(value) => (
                            <span className="text-[22px] md:text-[24px] font-semibold">
                              {formatCnt(value)}
                            </span>
                          )}
                        />
                      </Col>
                      <Col xs={12} lg={8}>
                        <Statistic
                          title={
                            <span className="text-[13px] text-[#8C8C8C]">
                              평균 재생수
                            </span>
                          }
                          value={influencerInfo.average_plays}
                          prefix={
                            <PlayCircleOutlined className="text-[#13C2C2] text-[22px] md:text-[24px]" />
                          }
                          formatter={(value) => (
                            <span className="text-[22px] md:text-[24px] font-semibold">
                              {formatCnt(value)}
                            </span>
                          )}
                        />
                      </Col>
                      <Col xs={12} lg={8}>
                        <Statistic
                          title={
                            <span className="text-[13px] text-[#8C8C8C]">
                              릴스 총 시청시간
                            </span>
                          }
                          value={influencerInfo.reels_total_watch_time}
                          prefix={
                            <ClockCircleOutlined className="text-[#722ED1] text-[22px] md:text-[24px]" />
                          }
                          formatter={(value) => (
                            <span className="text-[22px] md:text-[24px] font-semibold">
                              {formatCnt(value)}
                            </span>
                          )}
                        />
                      </Col>
                      <Col xs={12} lg={8}>
                        <Statistic
                          title={
                            <span className="text-[13px] text-[#8C8C8C]">
                              릴스 평균 시청시간
                            </span>
                          }
                          value={influencerInfo.reels_avg_watch_time}
                          prefix={
                            <ClockCircleOutlined className="text-[#531DAB] text-[22px] md:text-[24px]" />
                          }
                          formatter={(value) => (
                            <span className="text-[22px] md:text-[24px] font-semibold">
                              {formatCnt(value)}
                            </span>
                          )}
                        />
                      </Col>
                      <Col xs={12} lg={8}>
                        <Statistic
                          title={
                            <span className="text-[13px] text-[#8C8C8C]">
                              평균 댓글 수
                            </span>
                          }
                          value={influencerInfo.average_comments}
                          prefix={
                            <CommentOutlined className="text-[#1890FF] text-[22px] md:text-[24px]" />
                          }
                          formatter={(value) => (
                            <span className="text-[22px] md:text-[24px] font-semibold">
                              {formatCnt(value)}
                            </span>
                          )}
                        />
                      </Col>
                      <Col xs={12} lg={8}>
                        <Statistic
                          title={
                            <span className="text-[13px] text-[#8C8C8C]">
                              평균 저장 수
                            </span>
                          }
                          value={influencerInfo.average_collects}
                          prefix={
                            <BookOutlined className="text-[#FAAD14] text-[22px] md:text-[24px]" />
                          }
                          formatter={(value) => (
                            <span className="text-[22px] md:text-[24px] font-semibold">
                              {formatCnt(value)}
                            </span>
                          )}
                        />
                      </Col>
                      <Col xs={12} lg={8}>
                        <Statistic
                          title={
                            <span className="text-[13px] text-[#8C8C8C]">
                              평균 공유 수
                            </span>
                          }
                          value={influencerInfo.average_shares}
                          prefix={
                            <ShareAltOutlined className="text-[#52C41A] text-[22px] md:text-[24px]" />
                          }
                          formatter={(value) => (
                            <span className="text-[22px] md:text-[24px] font-semibold">
                              {formatCnt(value)}
                            </span>
                          )}
                        />
                      </Col>
                      <Col xs={12} lg={8}>
                        <Statistic
                          title={
                            <span className="text-[13px] text-[#8C8C8C]">
                              평균 도달 수
                            </span>
                          }
                          value={influencerInfo.average_reach}
                          prefix={
                            <TeamOutlined className="text-[#2F54EB] text-[22px] md:text-[24px]" />
                          }
                          formatter={(value) => (
                            <span className="text-[22px] md:text-[24px] font-semibold">
                              {formatCnt(value)}
                            </span>
                          )}
                        />
                      </Col>
                      <Col xs={12} lg={8}>
                        <Statistic
                          title={
                            <span className="text-[13px] text-[#8C8C8C]">
                              평균 노출 수
                            </span>
                          }
                          value={influencerInfo.average_impressions}
                          prefix={
                            <FundViewOutlined className="text-[#FA8C16] text-[22px] md:text-[24px]" />
                          }
                          formatter={(value) => (
                            <span className="text-[22px] md:text-[24px] font-semibold">
                              {formatCnt(value)}
                            </span>
                          )}
                        />
                      </Col>
                      <Col xs={12} lg={8}>
                        <Statistic
                          title={
                            <span className="text-[13px] text-[#8C8C8C]">
                              참여율
                            </span>
                          }
                          value={
                            influencerInfo.engagement?.toFixed(2) ?? "0.00"
                          }
                          prefix={
                            <ThunderboltOutlined className="text-[#52C41A] text-[22px] md:text-[24px]" />
                          }
                          formatter={(value) => (
                            <span className="text-[22px] md:text-[24px] font-semibold text-[#52C41A]">
                              {value}%
                            </span>
                          )}
                        />
                      </Col>
                    </Row>
                  </Card>
                </div>

                {/* 주간 추이 */}
                <div className="mb-8">
                  <SectionTitle>주간 추이</SectionTitle>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} lg={8}>
                      <TrendCard title="팔로워" data={trends.followers} />
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                      <TrendCard title="좋아요" data={trends.likes} />
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                      <TrendCard title="콘텐츠 수" data={trends.contents} />
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                      <TrendCard title="평균 조회수" data={trends.avgViews} />
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                      <TrendCard title="평균 재생수" data={trends.avgPlays} />
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                      <TrendCard
                        title="릴스 총 시청시간"
                        data={trends.totalWatchTime}
                      />
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                      <TrendCard
                        title="릴스 평균 시청시간"
                        data={trends.avgWatchTime}
                      />
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                      <TrendCard title="평균 도달 수" data={trends.avgReach} />
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                      <TrendCard
                        title="평균 노출 수"
                        data={trends.avgImpressions}
                      />
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                      <TrendCard title="평균 좋아요" data={trends.avgLikes} />
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                      <TrendCard
                        title="평균 댓글 수"
                        data={trends.avgComments}
                      />
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                      <TrendCard
                        title="평균 저장 수"
                        data={trends.avgCollects}
                      />
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                      <TrendCard title="평균 공유 수" data={trends.avgShares} />
                    </Col>
                  </Row>
                </div>

                {/* 최근 영상 */}
                {recentContents && recentContents.length > 0 && (
                  <div>
                    <Router.InstagramCarousel contents={recentContents} />
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
